import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../components/AuthProvider';
import { LayoutContext } from '../components/LayoutProvider';
import { showTemporaryMessage } from "../components/Message";
import logoicon from '../images/tuutuum-logo-icon.png';
import { update_scan_token } from "../tools-network";
import NeumorphicContainer from './NeumorphicContainer';

const WRISTBAND_COLORS = [
    { value: 'white', hex: '#FFFFFF', border: true },
    { value: 'black', hex: '#000000' },
    { value: 'red', hex: '#DC2626' },
    { value: 'blue', hex: '#2563EB' },
    { value: 'green', hex: '#16A34A' },
    { value: 'yellow', hex: '#EAB308' },
    { value: 'pink', hex: '#EC4899' },
    { value: 'purple', hex: '#9333EA' },
    { value: 'orange', hex: '#EA580C' }
];

const RegisterQrCodeStep3b = ({ selectedBearerId, bearerName, onPrevious, onNext }) => {
    const { t } = useTranslation();
    const { loginToken } = React.useContext(AuthContext);
    const { setIsHeaderVisible } = React.useContext(LayoutContext);

    useEffect(() => {
        setIsHeaderVisible(false);

        return () => setIsHeaderVisible(true);
    }, [setIsHeaderVisible]);

    const [selectedColor, setSelectedColor] = useState('white');

    // Generate possessive wristband name
    const wristbandName = bearerName ? t('wristband_name_format', { name: bearerName }) : t('give_wristband_color');

    const handlePrevious = async () => {
        console.log("Handle previous!");
        onPrevious();
    };

    const handleNext = async () => {
        console.log("Handle Next with color:", selectedColor);
        await setScanTokenColor();
        onNext();
    };

    const queryParams = new URLSearchParams(location.search);
    const pending_token = queryParams.get('pending_token');

    const setScanTokenColor = async () => {
        try {
            const updateFields = { color: selectedColor };
            const res = await update_scan_token(loginToken, pending_token, updateFields);
            if (res.success) {
                showTemporaryMessage(t("scan_token_color_successfully_updated"), "", 'success');
            } else {
                console.log("UPDATE SCAN TOKEN FAILED: " + JSON.stringify(res));
                return;
            }
        } catch (err) {
            console.log("Error updating scan code: " + err);
            showTemporaryMessage("Scan token information failed to update!", " ", 'fail');
            return;
        }
    };

    return (
        <NeumorphicContainer>
            <img src={logoicon} alt="Tuutuum Logo" className="block h-7 w-auto" />
            <h2 className="text-center text-2xl leading-tight px-3">
                {bearerName ? wristbandName : t("give_wristband_color")}
            </h2>
            <p className="text-center text-sm text-gray-600 mb-4">
                {t("optional_choose_color_matching_wristband")}
            </p>
            {/* Color picker */}
            <div className="flex flex-wrap w-full gap-3 justify-center pt-1 pb-4">
                {WRISTBAND_COLORS.map((color) => (
                    <button
                        key={color.value}
                        onClick={() => setSelectedColor(color.value)}
                        className={`w-16 h-16 rounded-full transition-all ${
                            selectedColor === color.value
                                ? 'ring-4 ring-cyan-600 scale-110'
                                : 'hover:scale-105'
                        } ${color.border ? 'border-2 border-gray-300' : ''}`}
                        style={{ backgroundColor: color.hex }}
                        title={t(`color_${color.value}`)}
                        aria-label={t(`color_${color.value}`)}
                    >
                        {selectedColor === color.value && (
                            <div className="flex items-center justify-center">
                                <svg
                                    className={`w-8 h-8 ${color.value === 'white' || color.value === 'yellow' ? 'text-gray-800' : 'text-white'}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={3}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                        )}
                    </button>
                ))}
            </div>
            <p className="text-center text-xs text-gray-500 mb-4">
                {t("currently_selected")}: <span className="font-semibold">{t(`color_${selectedColor}`)}</span>
            </p>
            {/* Buttons */}
            <div className="flex flex-nowrap w-full gap-4 justify-between">
                <button
                    className="btn-secondary btn-secondary:hover text-white px-3 py-1 w-auto rounded-md sm:leading-6 text-center font-semibold text-sm transition ease-in-out hover:scale-105"
                    onClick={handlePrevious}
                >
                    {t("previous")}
                </button>
                <button
                    className="btn-main btn-main:hover text-white px-3 py-1 w-auto rounded-md sm:leading-6 text-center font-semibold text-sm transition ease-in-out hover:scale-105"
                    onClick={handleNext}
                >
                    {t("next")}
                </button>
            </div>
        </NeumorphicContainer>
    );
}

export default RegisterQrCodeStep3b;
